/**
 * 单维卡片（2026-09-16 重新设计）
 *
 * 设计目标只有一句：**让本人或管理者 3 秒内拿到这一维的“习惯”和“洞察”**。
 * 所以卡内的信息按“读一眼 → 读三秒 → 想深究”三层排：
 *
 *   ① 一行头：维度名 + 分数三态（服务端给的，前端不重算）
 *   ② 主数字（hero）：这一维最该被记住的那一个数（HERO_FACT 点名的服务端事实行）
 *      —— 为什么不是主指标合计：“窗口内 25,044 千卡”没人读得懂，“记录了 48 次”一眼就懂习惯
 *   ③ 42 天一致性条：每 7 天一组、一格一天，有记录按量级上色 —— 读法是 GitHub contributions，
 *      3 秒回答“有规律吗、断在哪”（这是“习惯”唯一的可视化落点）
 *   ④ 三个关键数字：均值 / 最长连续 / 有记录天数（大号，扫一眼就够）
 *   ⑤ 其余指标：一行一个“名称 + 同款迷你条”，只画**有记录**的那些，没有记录的不占位
 *   ⑥ 洞察：1–2 句结论（conclusion.js，B 档：允许引外部参考值）
 *
 * 去掉的东西（“去掉不必要的描述性文字”）：逐条事实清单、长句提示、窗口天数重复说明
 * —— 那些数已经在 strip / 统计数字里，再说一遍只是把卡片堆高。
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'antd';
import { BaselineBand, DayStrip } from './MetricBars';
import { NoticeList } from './Notice';
import { conclusionsFor } from './conclusion';
import {
  DIMENSION_LABEL,
  DIMENSION_METRICS,
  HERO_FACT,
  MACRO_METRICS,
  PRIMARY_METRIC,
  isPageLevelBucket,
} from './registry';
import {
  contextOf,
  fill,
  scoreLine,
  FACT_LABEL,
  LABEL,
  PAGE_COPY,
  DEFINITION_COPY,
  UNKNOWN_TEXT,
  formatNumber,
  num,
} from './copy';
import {
  primarySeriesOf,
  dayBuckets,
  macroSplitOf,
  targetProgressOf,
} from './series';
import styles from './index.less';

const SCORE_CLASS = {
  normal: '',
  muted: styles.dimScoreMuted,
  provisional: styles.dimScoreProvisional,
};

/**
 * 目标进度：自设目标 + 实际值 + 差额 + 一根进度条（2026-09-17 从结论行搬进卡片）。
 * 只认 `source === 'user'`（`targetProgressOf` 里卡死），没设目标时整行不出现。
 */
function TargetRow({ progress, unit }) {
  const ratio = progress.ratio === null ? 0 : progress.ratio;
  const pct = Math.min(Math.max(ratio, 0), 1) * 100;
  return (
    <div className={styles.dimTarget} data-test="hh-v2-dim-target">
      <div className={styles.dimTargetHead}>
        <span className={styles.dimTargetTitle}>{LABEL.targetTitle}</span>
        <span className={styles.dimTargetGoal}>
          {formatNumber(progress.target)} {unit}
        </span>
        <span className={styles.dimTargetMeta}>
          {LABEL.targetActual(formatNumber(progress.actual), unit)}
        </span>
        <span className={styles.dimTargetMeta}>
          {progress.achieved
            ? LABEL.targetReached
            : LABEL.targetGap(formatNumber(Math.abs(progress.delta)), unit)}
        </span>
      </div>
      <div className={styles.dimTargetTrack}>
        <div className={styles.dimTargetFill} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

TargetRow.propTypes = {
  progress: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  unit: PropTypes.string,
};

TargetRow.defaultProps = { unit: '' };

const pctText = (share) => `${Math.round(share * 100)}%`;

/**
 * 三大营养素供能占比：一根堆叠条 + 图例（克数 + 占比）。
 *
 * 为什么按供能而不是克数：脂肪每克 9 千卡、蛋白质/碳水只有 4，按克数画会把脂肪画得很小，
 * 读者会读成“脂肪吃得少”——那是个错误的直观（换算系数已登记在 method.js 的 CITATIONS）。
 */
function MacroSplit({ parts }) {
  return (
    <div className={styles.macro} data-test="hh-v2-dim-macro">
      <div className={styles.macroTitle}>{LABEL.macroTitle}</div>
      <div className={styles.macroBar}>
        {parts.map((part, index) => (
          <span
            key={part.key}
            className={`${styles.macroSeg} ${styles[`macroTone${index}`]}`}
            style={{ width: `${part.share * 100}%` }}
          />
        ))}
      </div>
      <div className={styles.macroLegend}>
        {parts.map((part, index) => (
          <span key={part.key} className={styles.macroItem}>
            <i
              className={`${styles.macroDot} ${styles[`macroTone${index}`]}`}
            />
            <span className={styles.macroName}>{part.label}</span>
            <b className={styles.macroGrams}>{formatNumber(part.grams)}</b>
            <span className={styles.macroUnit}>{LABEL.macroGram}</span>
            <span className={styles.macroPct}>{pctText(part.share)}</span>
          </span>
        ))}
      </div>
      <div className={styles.macroNote}>{LABEL.macroNote}</div>
    </div>
  );
}

MacroSplit.propTypes = {
  parts: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
};

/** 主数字：优先取 HERO_FACT 点名的事实行；都没有就退回主指标的窗口合计 */
function heroOf(dimension, dim, primaryRows, metric) {
  const facts = Array.isArray(dim && dim.facts) ? dim.facts : [];
  const keys = HERO_FACT[dimension] || [];
  const hit = keys
    .map((key) =>
      facts.find(
        (fact) =>
          fact &&
          fact.key === key &&
          fact.observed !== false &&
          fact.value !== null &&
          fact.value !== '',
      ),
    )
    .find(Boolean);
  if (hit) {
    return {
      value: formatNumber(hit.value),
      unit: hit.unit || '',
      label: FACT_LABEL[hit.key] || '',
    };
  }
  // 退化路径：服务端没有那一行事实（理论上不会），至少给主指标的合计，不留空白
  let sum = 0;
  let count = 0;
  (primaryRows || []).forEach((row) => {
    if (row && row.observed === true && typeof row.value === 'number') {
      sum += row.value;
      count += 1;
    }
  });
  if (!count) return null;
  return {
    value: formatNumber(sum),
    unit: metric.unit || '',
    label: LABEL.heroTotal,
  };
}

/** 卡内提示条：只留这一维自己的码。`common.*` 是跨维口径，由页顶说一次。 */
function cardBuckets(dim) {
  const list = Array.isArray(dim && dim.buckets) ? dim.buckets : [];
  return list.filter((code) => !isPageLevelBucket(code));
}

/** 三个关键数字（大号）：缺值写“未采集”，绝不写 0。
 *
 * 2026-09-17：第一个数字的标签从光杆“均值”改成“{主指标名}均值”（如“睡眠时长均值”、
 * “训练时长均值”）—— 同一个词在四个维度指的是不同的量（时长？热量？），
 * 运营得自己猜“这是什么均值”。名称与单位都取 `PRIMARY_METRIC[dim]`，仍是单一出处。
 */
function keyStats(features, metric) {
  const mean = num(features.mean);
  const streak = num(features.maxStreak);
  const days = num(features.activeDays);
  return [
    {
      key: 'mean',
      label: `${metric.label || ''}${LABEL.meanSuffix}`,
      value: mean === null ? UNKNOWN_TEXT : formatNumber(mean),
      unit: mean === null ? '' : metric.unit,
    },
    {
      key: 'streak',
      label: LABEL.maxStreak,
      value: streak === null ? UNKNOWN_TEXT : String(streak),
      unit: streak === null ? '' : LABEL.dayUnit,
    },
    {
      key: 'days',
      label: LABEL.activeDaysLabel,
      value: days === null ? UNKNOWN_TEXT : String(days),
      unit: days === null ? '' : LABEL.dayUnit,
    },
  ];
}

export const DimensionCard = React.memo(function DimensionCard({
  dimension,
  dim,
  indicators,
  win,
  pageCtx,
  sections,
}) {
  const ctx = React.useMemo(
    () => ({ ...pageCtx, ...contextOf(dimension, dim) }),
    [pageCtx, dimension, dim],
  );
  const metric = PRIMARY_METRIC[dimension] || { label: '', unit: '' };
  // 分数三态只读服务端的 score 对象（不读已格式化的 ctx.score）：避免“串与数”两种入参形态
  const score = scoreLine(dim && dim.score);
  const features = (dim && dim.features) || {};
  const baseline = (dim && dim.baseline) || {};
  const label = DIMENSION_LABEL[dimension] || dimension;
  const showBand = baseline.source === 'individual';
  // 结论（2026-09-16 拍板档位 B）：允许引外部参考值。见 conclusion.js 的例外说明。
  const conclusions = conclusionsFor(dimension, dim, pageCtx.windowDays);

  const dayKeys = (win && win.dayKeys) || [];
  const primaryRows = React.useMemo(
    () => primarySeriesOf(indicators, metric.series, dayKeys),
    [indicators, metric.series, dayKeys],
  );
  const primaryDays = React.useMemo(
    () => dayBuckets(primaryRows),
    [primaryRows],
  );
  // 其余指标：只画有记录的，没记录的不占一行（卡片的密度要留给有信息的东西）
  const others = React.useMemo(
    () =>
      (DIMENSION_METRICS[dimension] || [])
        .filter((item) => item.series !== metric.series)
        .map((item) => {
          const days = dayBuckets(
            primarySeriesOf(indicators, item.series, dayKeys),
          );
          return {
            series: item.series,
            label: item.label,
            unit: item.unit,
            days,
            count: days.filter((day) => day.value !== null).length,
          };
        })
        .filter((item) => item.count > 0),
    [indicators, dimension, metric.series, dayKeys],
  );

  const hero = heroOf(dimension, dim, primaryRows, metric);
  const stats = keyStats(features, metric);
  // 目标进度（只认用户自设）：原来是结论里的一句话，2026-09-17 改成卡片里的一行 + 进度条
  const target =
    dimension === 'SPORT' ? targetProgressOf(dim, pageCtx.windowDays) : null;
  // 三大营养素供能占比（仅饮食）：同样是“已经在卡片里有数”的那一类，不占结论行
  const macros = React.useMemo(
    () =>
      dimension === 'DIET'
        ? macroSplitOf(indicators, MACRO_METRICS, dayKeys)
        : null,
    [dimension, indicators, dayKeys],
  );

  if (sections.definition) {
    // S0：只给定义卡 + 采集途径 + 一条空轨道，一个数字都不出现（§5.5）
    return (
      <Card
        size="small"
        className={styles.dimCard}
        title={`${label} · ${PAGE_COPY.definitionTitle}`}
      >
        <div className={styles.dimText}>{DEFINITION_COPY[dimension]}</div>
        <div className={styles.dimText}>{PAGE_COPY.collectHint}</div>
        <DayStrip days={primaryDays} unit={metric.unit} />
      </Card>
    );
  }

  return (
    <Card
      size="small"
      className={styles.dimCard}
      data-test={`hh-v2-dim-${dimension}`}
      title={
        <span className={styles.dimHead}>
          <span className={styles.dimTitle}>{label}</span>
          <span className={`${styles.dimScore} ${SCORE_CLASS[score.tone]}`}>
            {score.text}
          </span>
        </span>
      }
    >
      <NoticeList buckets={cardBuckets(dim)} ctx={ctx} />

      {hero ? (
        <div className={styles.dimHero}>
          <span className={styles.dimHeroValue}>{hero.value}</span>
          {hero.unit ? (
            <span className={styles.dimHeroUnit}>{hero.unit}</span>
          ) : null}
          <span className={styles.dimHeroLabel}>{hero.label}</span>
        </div>
      ) : null}

      <DayStrip days={primaryDays} unit={metric.unit} />

      <div className={styles.dimStats}>
        {stats.map((stat) => (
          <div className={styles.dimStat} key={stat.key}>
            <div className={styles.dimStatValue}>{stat.value}</div>
            <div className={styles.dimStatLabel}>
              {stat.label}
              {stat.unit ? `（${stat.unit}）` : ''}
            </div>
          </div>
        ))}
      </div>

      {target ? <TargetRow progress={target} unit={target.unit} /> : null}

      {macros ? <MacroSplit parts={macros} /> : null}

      {others.length ? (
        <div className={styles.dimOthers}>
          {others.map((item) => (
            <div className={styles.dimOtherRow} key={item.series}>
              <span className={styles.dimOtherName}>{item.label}</span>
              <DayStrip days={item.days} unit={item.unit} />
            </div>
          ))}
        </div>
      ) : null}

      {showBand ? (
        <div>
          <BaselineBand
            p25={baseline.p25}
            p50={baseline.p50}
            p75={baseline.p75}
            unit={metric.unit}
          />
        </div>
      ) : null}

      {conclusions.length ? (
        <div
          className={styles.dimInsight}
          data-test={`hh-v2-conclusion-${dimension}`}
        >
          {conclusions.map((text) => (
            <div className={styles.insightLine} key={text}>
              {text}
            </div>
          ))}
        </div>
      ) : null}

      {sections.progress ? (
        <div className={styles.dimText}>{fill(PAGE_COPY.progress, ctx)}</div>
      ) : null}
    </Card>
  );
});

DimensionCard.propTypes = {
  dimension: PropTypes.string.isRequired,
  // dim / pageCtx / sections 的形状由 guard.checkProfile 保证（整页在它之后才渲染），
  // 这里不重复声明整棵树：两处形状各写一遍，漂移时就只剩页面白屏来告诉你了。
  dim: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  indicators: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  win: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  pageCtx: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  sections: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

DimensionCard.defaultProps = { indicators: null, win: null };

export default DimensionCard;
